import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, Leaf, Clock, Shield, Zap, Phone, Mail, MapPin, ChevronDown, Play, Pause, Volume2, VolumeX } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/be660a94-51cd-4091-868c-eb6cd33cb994/images/45fba62da492ef9700d34091028b5786ad75511274eed06d2b1f67df6bbe7620.png";
const PRODUCT_IMG = "https://customer-assets.emergentagent.com/job_be660a94-51cd-4091-868c-eb6cd33cb994/artifacts/90endxs3_image.png";
const ATOMISEUR_IMG = "https://customer-assets.emergentagent.com/job_be660a94-51cd-4091-868c-eb6cd33cb994/artifacts/dwlp8h7x_image.png";
const PULSEUR_IMG = "https://customer-assets.emergentagent.com/job_be660a94-51cd-4091-868c-eb6cd33cb994/artifacts/mpifwpv9_image.png";
const COMPARE_IMG = "https://static.prod-images.emergentagent.com/jobs/be660a94-51cd-4091-868c-eb6cd33cb994/images/1ea7725976f93c23b927fcf4c14e1c0c99db21c03c8ff25600acb8901ee5265c.png";

const cultures = [
  "Abricotier", "Cerisier", "Prunier", "Pommier", "Poirier",
  "Vigne", "Grandes cultures", "Maraichage", "Autre"
];

export default function CaloriaLandingPage() {
  const [formData, setFormData] = useState({
    prenom: '', nom: '', telephone: '', email: '',
    commune: '', culture: '', surface: '', message: '', urgent: false
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRef = useRef(null);
  const formRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleVideo = () => {
    if (videoRef.current) {
      if (videoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setVideoPlaying(!videoPlaying);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.prenom || !formData.nom || !formData.telephone) {
      toast.error('Remplissez au moins votre nom, prenom et telephone.');
      return;
    }
    setSending(true);
    try {
      const resp = await fetch(`${API_URL}/api/caloria/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (resp.ok) {
        setSent(true);
        toast.success('Demande envoyee ! Notre equipe vous rappelle sous 24h.');
      } else {
        toast.error('Erreur lors de l\'envoi. Reessayez.');
      }
    } catch {
      toast.error('Erreur de connexion.');
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="caloria-landing">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Gel sur fleurs" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="bg-red-600 text-white border-0 mb-6 px-4 py-2 text-sm font-semibold animate-pulse">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Alerte gelees de printemps
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
              Protegez vos cultures
              <span className="block text-green-400 mt-2">avant qu'il ne soit trop tard.</span>
            </h1>
            <p className="text-lg text-gray-200 mb-8 max-w-xl">
              Caloria, le traitement antigel naturel a base d'huiles essentielles.
              Une pulverisation = <strong className="text-green-400">+4 °C de protection</strong> pendant <strong className="text-green-400">72 heures</strong>.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                data-testid="cta-hero-btn"
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-6 rounded-full shadow-xl"
                onClick={scrollToForm}
              >
                Je veux proteger mes cultures
                <ChevronDown className="ml-2 w-5 h-5 animate-bounce" />
              </Button>
            </div>
            <div className="flex gap-6 mt-8">
              <div className="flex items-center gap-2 text-gray-300 text-sm">
                <Leaf className="w-4 h-4 text-green-400" /> 100% naturel
              </div>
              <div className="flex items-center gap-2 text-gray-300 text-sm">
                <Shield className="w-4 h-4 text-green-400" /> Compatible Bio
              </div>
              <div className="flex items-center gap-2 text-gray-300 text-sm">
                <Clock className="w-4 h-4 text-green-400" /> 72h de protection
              </div>
            </div>
          </div>

          {/* Video Player */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-white/20 cursor-pointer group" onClick={toggleVideo}>
            <video
              ref={videoRef}
              className="w-full aspect-video object-cover"
              poster={HERO_IMG}
              loop
              muted
              playsInline
            >
              <source src="/videos/caloria_hero.mp4" type="video/mp4" />
            </video>
            <div className={`absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity ${videoPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}>
              <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center shadow-xl">
                {videoPlaying ? <Pause className="w-8 h-8 text-green-700" /> : <Play className="w-8 h-8 text-green-700 ml-1" />}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-green-800 py-6">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: "+4°C", label: "Protection thermique" },
            { value: "72h", label: "Duree d'action" },
            { value: "20 L/ha", label: "Vergers" },
            { value: "10 L/ha", label: "Vignes" },
          ].map((s, i) => (
            <div key={i}>
              <div className="text-3xl font-black text-white">{s.value}</div>
              <div className="text-green-200 text-sm mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <Badge className="bg-green-100 text-green-800 border-green-300 mb-4">Biostimulant naturel</Badge>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Caloria : votre bouclier thermique naturel
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Caloria est un produit phytosanitaire naturel a base d'huiles essentielles de <strong>romarin, lavande et thym</strong>,
              developpe par Maison Casia. Une seule pulverisation, et vos plantes gagnent jusqu'a +4 °C pendant 72 heures.
            </p>
            <div className="space-y-4">
              {[
                { icon: Leaf, title: "100% Naturel", desc: "Huiles essentielles, sans residus chimiques" },
                { icon: Shield, title: "Compatible Bio, HVE, Zero Residu", desc: "Conforme a toutes les certifications" },
                { icon: Zap, title: "Pret a l'emploi", desc: "Pas de melange, pas d'adjuvant. Pulverisez directement" },
                { icon: CheckCircle2, title: "Sans danger", desc: "Pour les cultures, l'environnement, les pollinisateurs et l'homme" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-green-700" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{item.title}</div>
                    <div className="text-sm text-gray-500">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img src={PRODUCT_IMG} alt="Caloria produit" className="w-full h-auto" />
          </div>
        </div>
      </section>

      {/* Application Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge className="bg-blue-100 text-blue-800 border-blue-300 mb-4">Application simple</Badge>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Aussi simple qu'un traitement classique
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              On applique avec votre materiel habituel. Vous couvrez toute votre surface en une matinee — 
              bien plus vite qu'avec des bougies, et pour bien moins cher.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Vergers - Atomiseur */}
            <div className="rounded-2xl overflow-hidden shadow-xl bg-white border">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={ATOMISEUR_IMG} alt="Atomiseur dans les vergers" className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-bold text-gray-900">Vergers — Atomiseur</h3>
                  <Badge className="bg-green-100 text-green-800 border-green-300">20 L/ha</Badge>
                </div>
                <p className="text-gray-600">
                  Application a l'atomiseur dans les rangs de vergers. Abricotiers, cerisiers, pruniers, 
                  pommiers, poiriers — traitement complet en quelques heures.
                </p>
              </div>
            </div>
            {/* Vignes - Pulseur */}
            <div className="rounded-2xl overflow-hidden shadow-xl bg-white border">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={PULSEUR_IMG} alt="Pulseur dans les vignes" className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-bold text-gray-900">Vignes — Pulseur</h3>
                  <Badge className="bg-purple-100 text-purple-800 border-purple-300">10 L/ha</Badge>
                </div>
                <p className="text-gray-600">
                  Application au pulseur entre les rangees de vignes. Efficace meme en conditions de 
                  gelee noire — pas seulement les gelees blanches classiques.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Bougies vs Caloria</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Vous utilisez encore des bougies ? Comptez le cout, le temps d'installation, 
              et la surface limitee que vous protegez.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="bg-red-950/50 border-red-800">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-red-400 mb-4">Bougies antigel</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start gap-2"><span className="text-red-400">x</span> Installation longue et penible</li>
                  <li className="flex items-start gap-2"><span className="text-red-400">x</span> Cout eleve par hectare</li>
                  <li className="flex items-start gap-2"><span className="text-red-400">x</span> Surface limitee</li>
                  <li className="flex items-start gap-2"><span className="text-red-400">x</span> Protection localisee uniquement</li>
                  <li className="flex items-start gap-2"><span className="text-red-400">x</span> Logistique lourde</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="bg-green-950/50 border-green-800">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-green-400 mb-4">Caloria</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" /> Pulverisation rapide (1 matinee)</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" /> Economique a grande echelle</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" /> Toute la surface protegee</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" /> +4°C pendant 72 heures</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" /> Zero logistique, pret a l'emploi</li>
                </ul>
              </CardContent>
            </Card>
          </div>
          <div className="mt-8 rounded-2xl overflow-hidden shadow-xl max-w-4xl mx-auto">
            <img src={COMPARE_IMG} alt="Comparaison bougies vs Caloria" className="w-full h-auto" />
          </div>
        </div>
      </section>

      {/* Video Gallery */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge className="bg-green-100 text-green-800 border-green-300 mb-4">En video</Badge>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Decouvrez Caloria en action</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { src: "/videos/caloria_hero.mp4", title: "Le gel sur vos cultures", desc: "Le scenario cauchemar des arboriculteurs" },
              { src: "/videos/caloria_atomiseur.mp4", title: "Application au verger", desc: "Traitement a l'atomiseur entre les rangs" },
              { src: "/videos/caloria_cta.mp4", title: "Temoignage terrain", desc: "Des annees d'experience a votre service" },
            ].map((v, i) => (
              <div key={i} className="rounded-2xl overflow-hidden shadow-lg bg-white">
                <video
                  className="w-full aspect-video object-cover cursor-pointer"
                  loop muted playsInline
                  onMouseEnter={(e) => e.target.play()}
                  onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                  poster={HERO_IMG}
                >
                  <source src={v.src} type="video/mp4" />
                </video>
                <div className="p-4">
                  <h3 className="font-bold text-gray-900">{v.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cultures Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Cultures protegees</h2>
          <p className="text-gray-500 mb-10">Caloria a fait ses preuves sur toutes ces cultures, y compris en gelee noire.</p>
          <div className="flex flex-wrap justify-center gap-4">
            {["Abricotier", "Cerisier", "Prunier", "Pommier", "Poirier", "Vigne", "Grandes cultures", "Maraichage"].map((c, i) => (
              <Badge key={i} variant="outline" className="px-6 py-3 text-base border-green-300 text-green-800 bg-green-50">
                {c}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section ref={formRef} className="py-20 bg-gradient-to-b from-green-50 to-white" id="formulaire">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-10">
            <Badge className="bg-red-100 text-red-800 border-red-300 mb-4">
              <AlertTriangle className="w-3 h-3 mr-1" /> Alerte gel imminente
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Je veux proteger mes cultures
            </h2>
            <p className="text-gray-500">
              Notre conseiller Casia vous rappelle sous 24h pour preparer votre commande de Caloria.
            </p>
          </div>

          {sent ? (
            <Card className="bg-green-50 border-green-300" data-testid="form-success">
              <CardContent className="p-10 text-center">
                <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-green-800 mb-2">Demande envoyee !</h3>
                <p className="text-green-600">Notre equipe Casia vous rappelle sous 24 heures pour organiser votre approvisionnement en Caloria.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="shadow-xl border-0" data-testid="caloria-form">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Prenom *</label>
                      <Input
                        data-testid="form-prenom"
                        placeholder="Jean"
                        value={formData.prenom}
                        onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                      <Input
                        data-testid="form-nom"
                        placeholder="Dupont"
                        value={formData.nom}
                        onChange={(e) => setFormData({...formData, nom: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Telephone *</label>
                      <Input
                        data-testid="form-telephone"
                        placeholder="06 12 34 56 78"
                        value={formData.telephone}
                        onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <Input
                        data-testid="form-email"
                        placeholder="jean@exploitation.fr"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Commune / Departement</label>
                    <Input
                      data-testid="form-commune"
                      placeholder="Ex : Nyons, Drome (26)"
                      value={formData.commune}
                      onChange={(e) => setFormData({...formData, commune: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Culture principale</label>
                      <Select value={formData.culture} onValueChange={(v) => setFormData({...formData, culture: v})}>
                        <SelectTrigger data-testid="form-culture">
                          <SelectValue placeholder="Choisir une culture..." />
                        </SelectTrigger>
                        <SelectContent>
                          {cultures.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Surface (hectares)</label>
                      <Input
                        data-testid="form-surface"
                        placeholder="Ex : 8 ha"
                        value={formData.surface}
                        onChange={(e) => setFormData({...formData, surface: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message (optionnel)</label>
                    <Textarea
                      data-testid="form-message"
                      placeholder="Questions, precisions, situation particuliere..."
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      rows={3}
                    />
                  </div>
                  <label className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="form-urgent"
                      checked={formData.urgent}
                      onChange={(e) => setFormData({...formData, urgent: e.target.checked})}
                      className="w-5 h-5 text-red-600 rounded"
                    />
                    <span className="text-sm text-red-800">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      Alerte gel imminente — j'ai besoin d'une reponse en urgence (sous 24h)
                    </span>
                  </label>
                  <Button
                    data-testid="form-submit-btn"
                    type="submit"
                    className="w-full bg-green-600 hover:bg-green-700 text-white text-lg py-6 rounded-full shadow-lg"
                    disabled={sending}
                  >
                    {sending ? 'Envoi en cours...' : 'Envoyer ma demande'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-10 text-center text-gray-400">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-lg font-semibold text-white mb-2">Maison Casia</p>
          <p className="text-sm">Agriculture naturelle — Biostimulants de 1ere generation</p>
          <p className="text-xs mt-4">Casia Bureau Agricole. Tous droits reserves.</p>
        </div>
      </footer>
    </div>
  );
}
